import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

interface ChartSync {
  /** Time under the pointer (or keyboard cursor) in any chart of the group. */
  t: number | null;
  /** Which chart the pointer is in; that chart shows the tooltip, the others only the crosshair. */
  source: string | null;
  set: (t: number | null, source: string | null) => void;
}

const ChartSyncContext = createContext<ChartSync | null>(null);

/** Charts inside share one crosshair, so a spike in one can be read against the others at the same moment. */
export function ChartSyncProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ t: number | null; source: string | null }>({ t: null, source: null });
  const set = useCallback((t: number | null, source: string | null) => setState({ t, source }), []);
  const value = useMemo(() => ({ ...state, set }), [state, set]);
  return <ChartSyncContext.Provider value={value}>{children}</ChartSyncContext.Provider>;
}

/** The group's crosshair, or a private one when the chart stands alone. */
export function useChartSync(): ChartSync {
  const shared = useContext(ChartSyncContext);
  const [local, setLocal] = useState<{ t: number | null; source: string | null }>({ t: null, source: null });
  const setOwn = useCallback((t: number | null, source: string | null) => setLocal({ t, source }), []);
  return shared ?? { ...local, set: setOwn };
}
